import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LiveConversationDetail, LiveStatusFilter } from '@manaro/contracts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import {
  fetchLiveConversation,
  fetchLiveConversations,
  fetchLiveSummary,
  runLiveAction,
  sendOperatorMessage,
  type LiveAction,
} from './api.ts'

export function useLiveSummary() {
  return useQuery({
    queryKey: queryKeys.liveSummary,
    queryFn: ({ signal }) => fetchLiveSummary(signal),
    refetchInterval: 15_000,
    staleTime: 10_000,
  })
}

export function useLiveConversations(filter: LiveStatusFilter) {
  return useQuery({
    queryKey: queryKeys.liveConversations(filter),
    queryFn: ({ signal }) => fetchLiveConversations(filter, signal),
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
    staleTime: 0,
  })
}

export function useLiveConversation(id: string | null) {
  return useQuery({
    queryKey: queryKeys.liveConversation(id ?? ''),
    queryFn: ({ signal }) => fetchLiveConversation(id as string, signal),
    enabled: Boolean(id),
    refetchInterval: 3_000,
    staleTime: 0,
  })
}

function useStoreDetail() {
  const queryClient = useQueryClient()
  return (detail: LiveConversationDetail) =>
    queryClient.setQueryData(queryKeys.liveConversation(detail.conversation.id), detail)
}

/** A poll that left before the action could land after it and put the old state back — drop it. */
function useCancelPolling(id: string) {
  const queryClient = useQueryClient()
  return () => queryClient.cancelQueries({ queryKey: queryKeys.liveConversation(id) })
}

export function useLiveAction(id: string) {
  const store = useStoreDetail()
  const cancelPolling = useCancelPolling(id)
  return useMutation({
    mutationFn: (action: LiveAction) => runLiveAction(id, action),
    onMutate: cancelPolling,
    onSuccess: store,
  })
}

export function useOperatorReply(id: string) {
  const store = useStoreDetail()
  const cancelPolling = useCancelPolling(id)
  return useMutation({
    mutationFn: (text: string) => sendOperatorMessage(id, text),
    onMutate: cancelPolling,
    onSuccess: store,
  })
}
